import { Component, ElementRef, Input, ViewChildren, QueryList, OnInit, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { take } from 'rxjs';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { highlightIllegalXmlChars, noIllegalXmlCharsValidator, scrollToIllegalXmlChar } from 'src/app/shared/validators/xml-validators';
import { ObsForm } from 'src/app/shared/models/obs-form.model';
import { MatExpansionPanel } from '@angular/material/expansion';
import { ResponseTypeEnum } from 'src/app/shared/enums/response-type.enum';
import { FsObservation } from 'src/app/shared/models/fs-observation.model';
import { ObsCategory } from 'src/app/shared/models/obs-category.model';

@Component({
  standalone: false,
  selector: 'app-obs-domains',
  templateUrl: './obs-domains.component.html',
  styleUrls: ['./obs-domains.component.scss']
})

export class ObsDomainsComponent implements OnInit, AfterViewInit {
  @Input() obsForm: ObsForm;
  @Input() fsObservation: FsObservation;
  @Input() obsAssessmentForm: FormGroup;
  @Input() completed: boolean;
  @Input() isTeach: boolean;
  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;
  @ViewChildren('notes') noteTextareas: QueryList<ElementRef>;
  @ViewChildren(CdkTextareaAutosize) autosizeList!: QueryList<CdkTextareaAutosize>;
  @Output() responseChanged:EventEmitter<{ questionId: number, response: any }> = new EventEmitter<{ questionId: number, response: any }>();

  obsDomainsForm: FormGroup;
  allPanelsExpanded = false;
  ResponseTypeEnum = ResponseTypeEnum;
  finishing: boolean;
  private readonly didNotObserveValue = 0;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    if (!this.obsForm || !this.obsForm.obsDomains || !this.fsObservation) {
      console.error('ObsDomainsComponent missing required inputs');
      return;
    }
    
    this.obsDomainsForm = this.fb.group({});
    this.createFormControls();
    this.mapResponsesToObsForm();
    this.obsAssessmentForm.addControl('obsDomains', this.obsDomainsForm);
    
    if (this.completed) {
      this.obsDomainsForm.disable();
    }
  }

  ngAfterViewInit(): void {
    this.triggerAutosizeResize();
    this.autosizeList.changes.subscribe(() => this.triggerAutosizeResize());
  }

  private triggerAutosizeResize(): void {
    setTimeout(() => this.autosizeList.forEach(a => a.resizeToFitContent(true)));
  }

  ngOnDestroy() {
    this.obsAssessmentForm.removeControl('obsDomains');
  }

  mapResponsesToObsForm() {
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        const responses = this.fsObservation.obsQuestionResponses.filter(res => res.obsCategoryId === category.id);
        if (responses.length === 0) {
          return;
        }
        category.obsQuestions.forEach(question => {
          let response = responses.find(res => res.id === question.id);
          if (response) {
            question.response = response.responseTypeId === ResponseTypeEnum.Rating
              ? (response.response != null && response.response !== '' ? Number(response.response) : null)
              : response.response;
            this.obsDomainsForm.get(question.id.toString())?.setValue(question.response);
          }
        });
      });
    });
  }

  createFormControls() {
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          const validators = question.responseTypeId === ResponseTypeEnum.FreeformResponse
            ? [noIllegalXmlCharsValidator]
            : [];
          const control = new FormControl(question.response || (question.responseTypeId === ResponseTypeEnum.Rating ? null : ''), validators);
          this.obsDomainsForm.addControl(question.id.toString(), control);

          // Subscribe to value changes
          control.valueChanges.subscribe(value => {
            question.response = value;
            if (question.responseTypeId === ResponseTypeEnum.Rating) {
              this.updateRequiredFreeformValidity(category);
            }
          });
        });
      });
    });
  }

  private updateRequiredFreeformValidity(category: ObsCategory) {
    category.obsQuestions
      .filter(question => question.responseTypeId === ResponseTypeEnum.FreeformResponse && question.required)
      .forEach(question => this.obsDomainsForm.get(question.id.toString())?.updateValueAndValidity());
  }

  private isDidNotObserveSelected(category: ObsCategory): boolean {
    if (this.isTeach) {
      return false;
    }
    const ratingQuestion = category.obsQuestions.find(question => question.responseTypeId === ResponseTypeEnum.Rating);
    if (!ratingQuestion) {
      return false;
    }
    const control = this.obsDomainsForm.get(ratingQuestion.id.toString());
    if (!control || control.value === null || control.value === '') {
      return false;
    }
    return Number(control.value) === this.didNotObserveValue;
  }

  private requiredOnFinishUnlessDidNotObserve(category: ObsCategory): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isDidNotObserveSelected(category)) {
        return null;
      }
      return this.requiredOnFinishValidator(control);
    };
  }

  requiredValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return { required: true };
    }
    return null;
  }

  // Helper method to filter ratings by ID (replaces pipe usage to fix Angular 19 dynamic component bug)
  getRatingById(ratingId: number): any[] {
    if (!this.obsForm?.obsRatings) {
      return [];
    }
    return this.obsForm.obsRatings.filter(rating => rating.id === ratingId);
  }

  private getValidRatingValues(obsRatingId: number): number[] {
    const ratings = this.getRatingById(obsRatingId);
    if (!ratings.length) return [];
    return ratings[0].obsRatingOptions?.map((o: any) => o.value) ?? [];
  }

  private validRatingOptionValidator(validValues: number[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null) return null;
      return validValues.includes(control.value) ? null : { invalidRating: true };
    };
  }

  updateValidators() {
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          const control = this.obsDomainsForm.get(question.id.toString());
          if (control) {
            if ((question.responseTypeId === ResponseTypeEnum.FreeformResponse && !question.required) ||
                (question.responseTypeId === ResponseTypeEnum.Acknowledgement && !question.required)) {
                  control.updateValueAndValidity();
            }
            else if (question.responseTypeId === ResponseTypeEnum.FreeformResponse && question.required) {
              control.setValidators([this.requiredOnFinishUnlessDidNotObserve(category), noIllegalXmlCharsValidator]);
              control.updateValueAndValidity();
            }
            else {
              if (question.responseTypeId === ResponseTypeEnum.Rating) {
                const validValues = question.obsRatingId != null ? this.getValidRatingValues(question.obsRatingId) : [];
                control.setValidators([this.requiredOnFinishValidator.bind(this), this.validRatingOptionValidator(validValues)]);
              } else {
                control.setValidators(this.requiredOnFinishValidator.bind(this));
              }
              control.updateValueAndValidity();
            }
          }
        });
      });
    });
  }

  requiredOnFinishValidator(control: AbstractControl): ValidationErrors | null {
    if (this.finishing && (control.value === null || !this.isNonEmptyString(control.value))) {
      return { required: true };
    }
    return null;
  }

  isNonEmptyString(value: any): boolean {
    if (typeof value != 'string'){
      return true;
    }
    else {
      return typeof value === 'string' && value.trim().length > 0;
    }
  }

  validateForm(isFinishing: boolean = false): string[] {
    let errors = [];
    this.finishing = isFinishing;
    this.markFormGroupsAsTouched(this.obsDomainsForm);
    this.updateValidators();

    if (this.finishing && this.obsDomainsForm.invalid) {

      const errorMessages = this.getErrorMessages();

      errors = errors.concat(errorMessages);
    } else {
      this.finishing = false;
    }

    errors = errors.concat(this.getXmlCharErrorMessages());

    return errors;
  }

  getErrorMessages(): string[] {
    let errors: string[] = [];
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          const control = this.obsDomainsForm.get(question.id.toString());
          if (control && control.errors) {
            if (control.errors.required || (control.errors.invalidRating && question.responseTypeId === ResponseTypeEnum.Rating)) {
              if (question.responseTypeId === ResponseTypeEnum.Rating) {
                errors.push(`${category.description} proficiency rating is required.`);
              } else {
                errors.push(`${category.description} ${question.description.toLowerCase()} is required.`);
              }
            }
          }
        });
      });
    });
    return errors;
  }

  getXmlCharErrorMessages(): string[] {
    const errors: string[] = [];
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          if (question.responseTypeId === ResponseTypeEnum.FreeformResponse) {
            const control = this.obsDomainsForm.get(question.id.toString());
            if (control && control.errors?.illegalXmlChars) {
              errors.push(`${category.description} ${question.description.toLowerCase()} contains unknown characters.`);
            }
          }
        });
      });
    });
    return errors;
  }

  scrollToFirstFieldError(): Promise<boolean> {
    // Build a flat list of freeform questions paired with their domain index so we
    // can find and open the containing expansion panel if it is collapsed.
    const freeformQuestions: { question: any; domainIndex: number; textareaIndex: number }[] = [];
    let textareaIndex = 0;
    this.obsForm.obsDomains.forEach((domain, domainIndex) => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          if (question.responseTypeId === ResponseTypeEnum.FreeformResponse) {
            freeformQuestions.push({ question, domainIndex, textareaIndex });
            textareaIndex++;
          }
        });
      });
    });

    const textareas = this.noteTextareas.toArray();
    const panels = this.panels.toArray();

    for (const entry of freeformQuestions) {
      const control = this.obsDomainsForm.get(entry.question.id.toString());
      if (control?.errors) {
        const panel = panels[entry.domainIndex];
        const value = control.value;
        const hasXmlError = !!control.errors.illegalXmlChars;

        const scrollAndHighlight = (textarea: HTMLTextAreaElement) => {
          if (hasXmlError) {
            highlightIllegalXmlChars(textarea, value);
          } else {
            textarea.scrollIntoView({ block: 'center' });
          }
        };

        if (panel && !panel.expanded) {
          panel.open();
          // Re-query noteTextareas after expansion — the textarea is not in the DOM
          // until the panel opens (MatExpansionPanel uses *ngIf internally).
          return new Promise<boolean>(resolve => {
            panel.afterExpand.pipe(take(1)).subscribe(() => {
              requestAnimationFrame(() => {
                const textarea = this.noteTextareas.toArray()[entry.textareaIndex]?.nativeElement;
                if (textarea) {
                  scrollAndHighlight(textarea);
                }
                resolve(true);
              });
            });
          });
        } else {
          const textarea = textareas[entry.textareaIndex]?.nativeElement;
          if (textarea) {
            scrollAndHighlight(textarea);
          }
          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  }

  onFreeformBlur(questionId: number): void {
    const control = this.obsDomainsForm.get(questionId.toString());
    if (!control?.errors?.illegalXmlChars) {
      return;
    }
    const freeformQuestions: { id: number }[] = [];
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          if (question.responseTypeId === ResponseTypeEnum.FreeformResponse) {
            freeformQuestions.push(question);
          }
        });
      });
    });
    const index = freeformQuestions.findIndex(q => q.id === questionId);
    const textarea = this.noteTextareas.toArray()[index]?.nativeElement;
    if (textarea) {
      scrollToIllegalXmlChar(textarea, control.value);
    }
  }

  markFormGroupsAsTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control.markAsTouched({ onlySelf: true });
      if (control instanceof FormGroup) {
        this.markFormGroupsAsTouched(control);
      }
    });
    formGroup.markAsTouched();
  }

  toggleAllPanels() {
    this.allPanelsExpanded = !this.allPanelsExpanded;
    this.panels.forEach(panel => this.allPanelsExpanded ? panel.open() : panel.close());
  }

  updateObsFormWithLatestData() {
    this.obsForm.obsDomains.forEach(domain => {
      domain.obsCategories.forEach(category => {
        category.obsQuestions.forEach(question => {
          const control = this.obsDomainsForm.get(question.id.toString());
          if (control) {
            question.response = control.value;
          }
        });
      });
    });
  }

  private previousNoteControls: Map<string, FormControl> = new Map();

  getPreviousNoteForQuestion(questionId: number): string | null {
    const response = this.fsObservation?.previousObsQuestionResponses?.find(r => r.id === questionId);
    return response?.response || null;
  }

  getDisabledControl(value: string): FormControl {
    if (!this.previousNoteControls.has(value)) {
      const control = new FormControl({ value, disabled: true });
      this.previousNoteControls.set(value, control);
    }
    return this.previousNoteControls.get(value);
  }

  getPreviousRatingLabelForQuestion(questionId: number, obsRatingId: number): string | null {
    const response = this.fsObservation?.previousObsQuestionResponses?.find(r => r.id === questionId);
    if (!response?.response) return null;
    const numericValue = Number(response.response);
    const ratings = this.getRatingById(obsRatingId);
    if (!ratings.length) return null;
    const option = ratings[0].obsRatingOptions?.find((o: any) => o.value === numericValue);
    return option ? this.getRatingHeading(option.description) : null;
  }

  hasActivities(category: ObsCategory): boolean {
    return category.obsQuestions.some(question =>  question.responseTypeId === ResponseTypeEnum.Acknowledgement);
  }

  getRatingHeading(description: string): string {
    if (!description) return '';
    const colonIndex = description.indexOf(':');
    return colonIndex > -1 ? description.substring(0, colonIndex) : '';
  }

  getRatingText(description: string): string {
    if (!description) return '';
    const colonIndex = description.indexOf(':');
    return colonIndex > -1 ? description.substring(colonIndex + 1).trim() : description;
  }
}
